import React, { FC, useState } from 'react';
import { useQuery } from 'react-query';
import { Fade, Grid, Box, FormControl } from '@mui/material';
import {
  Modal,
  Loader,
  ModalSaveSection,
  AutoCompleteMultiple,
  ISelectOption
} from '../../../components';
import { useSnackbar } from 'notistack';
import { IDropdownResponse } from '../../../models';
import {
  getDynamicDocumentationFileTemplatesFinancingTypes,
  uploadDynamicDocumentationFileTemplate
} from '../../../fetch';
import { FileManagementUpload } from './file-management-upload';

interface IDynamicFileManagementAddFile {
  open: boolean;
  onClose: (shouldReload?: boolean) => void;
}
export const DynamicFileManagementAddFile: FC<IDynamicFileManagementAddFile> = ({
  open,
  onClose
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<ISelectOption[]>([]);
  const [fileData, setFileData] = useState<File | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading: isLoadingFinancingTypes, data: financingTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(
    ['getDynamicDocumentationFileTemplatesFinancingTypes'],
    async () => await getDynamicDocumentationFileTemplatesFinancingTypes(),
    {
      enabled: open
    }
  );

  const handleClose = (shouldReload?: boolean) => {
    setSelectedDocumentTypes([]);
    setFileData(null);
    onClose(shouldReload);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('TemplateFile', fileData);
      
      // Append each financing type as a separate entry
      selectedDocumentTypes.forEach((type, index) => {
        formData.append(`FinancingTypes[${index}]`, type.value);
      });

      if(selectedDocumentTypes.find(x=> x.value === '-1')) {
        formData.append(`IsApplicableToAll`, 'true');
      }
      
      await uploadDynamicDocumentationFileTemplate(formData);
      setIsSaving(false);
      enqueueSnackbar('Success, file saved!', {
        variant: 'success'
      });
      handleClose(true);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error adding file, please try again.`, {
        variant: 'error'
      });
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal
        title="Add File"
        open={open}
        onClose={() => {
          handleClose();
        }}
        maxWidth="xs"
      >
        {isSaving && <Loader type="overlay" position="centered" />}
        <Fade in={open}>
          <Box mt={1}>
            <Grid container>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small" required>
                  <AutoCompleteMultiple
                    name="financingType"
                    options={
                      financingTypes
                        ? financingTypes
                            // remove selected financing types from the list upon selection
                            .filter(
                              type => !selectedDocumentTypes.map(t => t.value).includes(type.value)
                            )
                            .map(type => ({
                              value: type.value,
                              label: type.description
                            }))
                        : []
                    }
                    label={isLoadingFinancingTypes ? 'Loading...' : 'Financing Type(s)'}
                    value={selectedDocumentTypes}
                    required
                    onChange={(event, newValue) => {
                      if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
                        setSelectedDocumentTypes([]);
                      } else if (Array.isArray(newValue)) {
                        setSelectedDocumentTypes(newValue.map(option => option));
                      }
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Grid container alignItems="center">
                  <Grid item xs={12}>
                    <FileManagementUpload
                      setFileData={value => setFileData(value)}
                      uploadText={'Upload File'}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <ModalSaveSection
              isSaveDisabled={selectedDocumentTypes?.length === 0 || !fileData}
              handleCancel={() => {
                handleClose();
              }}
              handleSave={() => {
                handleSave();
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
